import React, { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ChevronRight, FileText, Loader2, RefreshCw, Scale } from 'lucide-react';
import { useFieldToday, type FieldTask } from '../../hooks/useFieldToday';
import TaskCard from '../../components/field/TaskCard';
import TaskFilterChips from '../../components/field/TaskFilterChips';
import { useAuth } from '../../contexts/AuthContext';

type FilterKey = 'all' | 'trucking' | 'surface_rentals';

export default function TodayPage() {
  const { data, isLoading, isError, refetch, isFetching } = useFieldToday();
  const [filter, setFilter] = useState<FilterKey>('all');
  const navigate = useNavigate();
  const { hasVertical } = useAuth();

  // Cache for offline resilience
  const cachedRef = useRef(data);
  if (data) cachedRef.current = data;

  const displayData = data ?? cachedRef.current;
  const tasks = displayData?.tasks ?? [];

  const filteredTasks = filter === 'all' ? tasks : tasks.filter((t) => t.vertical === filter);
  const inProgress = filteredTasks.filter((t) => t.status === 'in_progress');
  const upNext = filteredTasks.filter((t) => t.status === 'scheduled');

  const counts = useMemo(
    () => ({
      all: tasks.length,
      trucking: tasks.filter((t) => t.vertical === 'trucking').length,
      surface_rentals: tasks.filter((t) => t.vertical === 'surface_rentals').length,
    }),
    [tasks],
  );

  return (
    <div className="space-y-5">
      {/* Offline/error banner */}
      {isError && !displayData && (
        <div className="bg-red-900/30 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-300">
          Unable to load tasks. Check your connection.
        </div>
      )}
      {isError && displayData && (
        <button
          onClick={() => refetch()}
          className="w-full flex items-center justify-center gap-2 bg-fieldiq-accent/10 border border-fieldiq-accent/20 rounded-lg px-4 py-2 text-sm text-fieldiq-accent"
        >
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
          Failed to refresh — tap to retry
        </button>
      )}

      {/* Filter chips */}
      <TaskFilterChips active={filter} counts={counts} onChange={setFilter} />

      {/* Driver actions. /driver/bols and /driver/weight-ticket/new were routed
        * in this tree and linked only from DriverBottomNav, which the /my-work
        * shell never renders — so filing a weight ticket was unreachable. A
        * positive vertical check: a rental tech has no BOLs and no scale. */}
      {hasVertical('trucking') && (
        <div className="grid grid-cols-2 gap-2.5">
          <button
            onClick={() => navigate('/driver/weight-ticket/new')}
            className="flex items-center justify-between gap-2 rounded-xl border border-fieldiq-border bg-fieldiq-card p-3.5 text-left"
          >
            <span className="flex items-center gap-2 min-w-0">
              <Scale className="h-4 w-4 shrink-0 text-fieldiq-accent" />
              <span className="text-sm font-medium truncate">Weight Ticket</span>
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-fieldiq-text" />
          </button>
          <button
            onClick={() => navigate('/driver/bols')}
            className="flex items-center justify-between gap-2 rounded-xl border border-fieldiq-border bg-fieldiq-card p-3.5 text-left"
          >
            <span className="flex items-center gap-2 min-w-0">
              <FileText className="h-4 w-4 shrink-0 text-fieldiq-accent" />
              <span className="text-sm font-medium truncate">My BOLs</span>
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-fieldiq-text" />
          </button>
        </div>
      )}

      {/* Loading */}
      {isLoading && !displayData && (
        <div className="flex justify-center py-12">
          <Loader2 size={28} className="animate-spin text-fieldiq-text" />
        </div>
      )}

      {/* Task sections */}
      {displayData && (
        <>
          {inProgress.length > 0 && (
            <section>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-fieldiq-text mb-2">
                In progress
              </p>
              <div className="space-y-2.5">
                {inProgress.map((task) => (
                  <TaskCard key={task.id} task={task} />
                ))}
              </div>
            </section>
          )}

          <section>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-fieldiq-text mb-2">
              Up next
            </p>
            <div className="space-y-2.5">
              {upNext.map((task) => (
                <TaskCard key={task.id} task={task} />
              ))}
              {upNext.length === 0 && filteredTasks.length === 0 && (
                <div className="text-center py-12">
                  <Calendar size={40} className="mx-auto text-fieldiq-text/40 mb-3" />
                  <p className="text-sm text-fieldiq-text">No work assigned for today</p>
                  <button
                    onClick={() => navigate('/my-work/schedule')}
                    className="mt-3 text-fieldiq-accent text-sm font-medium"
                  >
                    View upcoming schedule
                  </button>
                </div>
              )}
              {upNext.length === 0 && filteredTasks.length > 0 && (
                <p className="text-sm text-fieldiq-text py-6 text-center">No more tasks today.</p>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
